import React, { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import RichHtmlEditor from "modules/swiftboard/components/ui/RichHtmlEditor";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import apiEmailTemplate from "../../api";
import EmailHtmlPreviewFrame from "../EmailHtmlPreviewFrame";

const inputClass = (hasError) =>
  `w-full px-3 py-2 text-sm rounded-lg border bg-surface_alt text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition ${
    hasError ? "border-danger" : "border-border"
  }`;

const StepContentEditor = ({
  formData,
  setField,
  errors,
  placeholders,
  insertFields,
  contentMethod,
  generateWithAI,
  aiGenerating,
}) => {
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!formData.body_html?.trim()) {
        setPreviewHtml("");
        return;
      }
      setPreviewLoading(true);
      try {
        const res = await apiEmailTemplate.renderPreview({
          subject: formData.subject,
          eyebrow: formData.eyebrow,
          heading: formData.heading,
          body_html: formData.body_html,
          data_points: {},
        });
        setPreviewHtml(res.data?.data?.html || "");
      } catch {
        // Preview is a convenience — silently skip on failure, editing still works.
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [formData.subject, formData.eyebrow, formData.heading, formData.body_html]);

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-text_secondary mb-1.5">
            Subject <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={formData.subject || ""}
            onChange={(e) => setField("subject", e.target.value)}
            placeholder="e.g. Your project {{project_name}} has been approved"
            className={inputClass(errors.subject)}
          />
          {errors.subject && <p className="text-[11px] text-danger mt-1">{errors.subject}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text_secondary mb-1.5">Eyebrow Text</label>
            <input
              type="text"
              value={formData.eyebrow || ""}
              onChange={(e) => setField("eyebrow", e.target.value)}
              placeholder="PROJECT UPDATE"
              className={inputClass(false)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text_secondary mb-1.5">Heading</label>
            <input
              type="text"
              value={formData.heading || ""}
              onChange={(e) => setField("heading", e.target.value)}
              placeholder="Great news, {{first_name}}!"
              className={inputClass(false)}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-text_secondary">
              Body <span className="text-danger">*</span>
            </label>
            {contentMethod === "ai" && (
              <button
                type="button"
                onClick={() => setConfirmRegenerate(true)}
                disabled={aiGenerating}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary disabled:opacity-50"
              >
                <RefreshCw size={11} /> Regenerate with AI
              </button>
            )}
          </div>
          <RichHtmlEditor
            value={formData.body_html}
            onChange={(html) => setField("body_html", html)}
            placeholders={insertFields || placeholders}
            minHeight={280}
          />
          {errors.body_html && <p className="text-[11px] text-danger mt-1">{errors.body_html}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-text_secondary mb-1.5">
            Description <span className="text-text_muted font-normal">(internal note)</span>
          </label>
          <textarea
            value={formData.description || ""}
            onChange={(e) => setField("description", e.target.value)}
            rows={2}
            placeholder="Notes about which placeholders this template expects"
            className={inputClass(false)}
          />
        </div>
      </div>

      <div className="flex flex-col border border-border rounded-lg overflow-hidden bg-surface_alt min-h-[380px]">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface flex-shrink-0">
          <span className="text-xs font-semibold text-text_primary">Live Preview</span>
          {previewLoading && <Loader2 size={13} className="animate-spin text-text_muted" />}
        </div>
        <div className="flex-1">
          <EmailHtmlPreviewFrame html={previewHtml} emptyLabel="Start typing to see a live preview." />
        </div>
      </div>

      {confirmRegenerate && (
        <ConfirmDialogue
          confirmData={{
            title: "Regenerate with AI?",
            message: "This will overwrite your current subject, heading, and body content with a new AI-generated draft.",
            confirmText: "Regenerate",
            variant: "warning",
          }}
          handleConfirmClose={(confirmed) => {
            setConfirmRegenerate(false);
            if (confirmed) generateWithAI();
          }}
        />
      )}
    </div>
  );
};

export default StepContentEditor;
